import moment from 'moment';
import { attributeTypes } from 'utils';
import { isAutoSignature } from '../formDetail/signatureFill.constants';
import { inputTypeEnums } from '../formDetail/attributes/constnats';
import { hasInlineFields } from './inlineFields';

/**
 * Note fill flow helpers (`ReportBuilder`):
 * - which fields staff fill in, and whether each one is filled, for the
 *   "X completed · Y to fill" line and the "Show only fields to fill" toggle;
 * - the text a read-only input shows in its locked grey box.
 */

const htmlToText = (html) =>
    String(html ?? '')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/\s+/g, ' ')
        .trim();

/**
 * Types a read-only field shows as a locked box: inputs, selects, checkboxes,
 * radios, uploads, drawn signatures and Rich Text (not Goal Mode, whose text
 * repeats per picked goal, nor one with inline fields, which stay fillable).
 * Tables keep their grid, locked in place.
 */
export const isReadOnlyBoxType = (item) =>
    [attributeTypes.INPUT, attributeTypes.SELECT, attributeTypes.CHECK_BOX, attributeTypes.RADIO_BUTTON, attributeTypes.UPLOAD].includes(
        item?.type,
    ) ||
    (item?.type === attributeTypes.SIGNATURE && !isAutoSignature(item?.properties)) ||
    // A read-only Rich Text with inline fields keeps its fields fillable (`InlineFieldsRichText`): only the text is locked.
    (item?.type === attributeTypes.RICH_TEXT && !item?.properties?.goalMode && !hasInlineFields(item?.value));

const IMAGE_FILE_RE = /\.(svg|jpe?g|png|gif|webp|bmp)$/i;

/** An uploaded file or a signature that can be drawn as an image: its url, else ''. */
export const readOnlyBoxImageUrl = (item, value) => {
    if (!value?.url) return '';
    if (item?.type === attributeTypes.SIGNATURE) return value.url;
    if (item?.type !== attributeTypes.UPLOAD) return '';
    return IMAGE_FILE_RE.test(value?.originalName || value?.name || value?.url || '') ? value.url : '';
};

/** Rich Text with something to show: text or an image. */
export const hasRichTextContent = (html) => typeof html === 'string' && (htmlToText(html).length > 0 || /<img\s/i.test(html));

/** The value a read-only box shows, as plain text; '' when there is none. Rich Text is drawn as html instead. */
export const readOnlyBoxText = (item, value) => {
    if (value === null || value === undefined || value === '') return '';
    if (item?.type === attributeTypes.UPLOAD) return value?.originalName || value?.name || '';
    if (item?.type === attributeTypes.SIGNATURE) return '';
    if ([attributeTypes.SELECT, attributeTypes.CHECK_BOX, attributeTypes.RADIO_BUTTON].includes(item?.type)) {
        const list = Array.isArray(value) ? value : [value];
        return list
            .map((opt) => (typeof opt === 'object' ? opt?.name || opt?.value || '' : String(opt)))
            .filter(Boolean)
            .join(', ');
    }
    if (item?.defaultDateType === inputTypeEnums.DATE) {
        const date = moment(value);
        return date.isValid() ? date.format('MM/DD/YYYY') : String(value);
    }
    // A stored time is an ISO date-time; a typed one is already `HH:mm`.
    if (item?.defaultDateType === inputTypeEnums.TIME) return String(value).length > 10 ? String(value).slice(11, 16) : String(value);
    return typeof value === 'object' ? '' : htmlToText(value);
};

/**
 * A field staff fill in: an editable input, select, checkbox, radio, file
 * upload, plain Rich Text or drawn signature. Read-only fields, AI Summary
 * text, Goal Mode blocks, auto signatures and display blocks (tables, graphs,
 * session data, targets) are left out.
 */
export const isFillableAttribute = (item, aiBridge) => {
    if (!item) return false;
    // A read-only Rich Text with inline fields is still filled: only its text is locked.
    if (item?.properties?.readOnly && !(item.type === attributeTypes.RICH_TEXT && hasInlineFields(item?.value))) return false;
    switch (item.type) {
        case attributeTypes.INPUT:
        case attributeTypes.UPLOAD:
            return true;
        case attributeTypes.SELECT:
        case attributeTypes.CHECK_BOX:
        case attributeTypes.RADIO_BUTTON:
            return !!item?.properties?.options?.length;
        case attributeTypes.RICH_TEXT:
            return !item?.properties?.goalMode && !(aiBridge && item?.properties?.aiSummary);
        case attributeTypes.SIGNATURE:
            return !isAutoSignature(item?.properties);
        default:
            return false;
    }
};

/**
 * An editable table: filled cell by cell (required cells block saving), so it stays visible with
 * "Show only fields to fill" — but it is a grid, not one value, so it is not counted as a field.
 */
export const isEditableTable = (item) => item?.type === attributeTypes.TABLE && !item?.properties?.readOnly;

/** Whether a fillable field has a value. */
export const isAttributeFilled = (value) => {
    if (value === null || value === undefined) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.values(value).some((v) => v !== null && v !== undefined && v !== '');
    if (typeof value === 'string') return htmlToText(value).length > 0 || /<img\s/i.test(value);
    return true;
};
